import 'url-search-params-polyfill';

import { roundDownToMultipleOf60 } from './helpers';

export interface CanonicalUrl {
  fullUrl: string;
  relativeUrl: string;
}

const removeBrandNameAtBeginning = (text: string, brandName: string) => {
  if (text.toLowerCase().startsWith(brandName.toLowerCase())) {
    let res = text.substring(brandName.length);
    if (res.charAt(0) === ' ') {
      res = res.substring(1);
    }
    return res;
  }
  return text;
};

export const getImageAlt = ({ model, brandName, color }: { brandName: string; color?: string; model: string }) => {
  if (color) {
    return `${brandName} ${removeBrandNameAtBeginning(model, brandName)} - ${color}`;
  }
  return `${brandName} ${removeBrandNameAtBeginning(model, brandName)}`;
};

export const cutWordsToLimit = (str, maxLen, separator) => {
  if (str.length <= maxLen) return str;
  return str.substr(0, str.lastIndexOf(separator, maxLen));
};

export const minimizeCharactersCount = (str, maxLen) => {
  if (str.length <= maxLen) {
    return str;
  }

  const strWithoutMultipleSpaces = str.replace(/(\s|(\r\n)|\n|\r)+/gm, ' ');
  return cutWordsToLimit(strWithoutMultipleSpaces, maxLen, ' ');
};

export const removeHtmlTags = str => {
  const strWithBrReplaced = str.replace(/<\s*br\s*\/>|<\s*br\s*>/gm, '\n');
  return strWithBrReplaced.replace(/<\/?[^>]+(>|$)/gm, '');
};

const keys = {
  brands: ['brands', 'brands[]'],
  categories: ['categories', 'categories[]'],
  colors: ['colors', 'colors[]'],
  heelHeights: ['heel_heights', 'heel_heights[]'],
  onSale: ['on_sale'],
  priceGte: ['price_gte'],
  priceLte: ['price_lte'],
  query: ['q'],
  seasons: ['seasons', 'seasons[]'],
  sizes: ['sizes', 'sizes[]'],
  skip: ['skip'],
  sleeveLengths: ['sleeve_lengths', 'sleeve_lengths[]'],
  sortField: ['sort_field'],
  toeTypes: ['toe_types', 'toe_types[]'],
};

export const defaultQueryValues = {
  /* eslint-disable @typescript-eslint/camelcase */
  limit: ['60'],
  on_sale: ['0'],
  q: [''],
  skip: ['0'],
  sort_field: ['insert_time'],
  /* eslint-enable @typescript-eslint/camelcase */
};

export const limitedCanonicalParams = {
  sort_field: ['insert_time', 'price_desc', 'price_asc'],
};

export const orderedCanonicalParams: [string[], string][] = [
  [keys.onSale, 'on_sale'],
  [keys.categories, 'categories[]'],
  [keys.sleeveLengths, 'sleeve_lengths[]'],
  [keys.heelHeights, 'heel_heights[]'],
  [keys.toeTypes, 'toe_types[]'],
  [keys.brands, 'brands[]'],
  [keys.seasons, 'seasons[]'],
  [keys.sizes, 'sizes[]'],
  [keys.colors, 'colors[]'],
  [keys.sortField, 'sort_field'],
  [keys.query, 'q'],
  [keys.skip, 'skip'],
  [keys.priceGte, 'price_gte'],
  [keys.priceLte, 'price_lte'],
];

export const removeDefaultParams = (queryParams: Record<string, string[]>) => {
  const paramsWithoutDefaultValues: Record<string, string[]> = {};

  Object.keys(queryParams).forEach(queryParamKey => {
    const queryParamValues = queryParams[queryParamKey];
    queryParamValues.forEach(val => {
      const lowercaseParamValue = val.toLowerCase();

      if (!defaultQueryValues[queryParamKey] || !defaultQueryValues[queryParamKey].includes(lowercaseParamValue)) {
        if (!paramsWithoutDefaultValues[queryParamKey]) {
          paramsWithoutDefaultValues[queryParamKey] = [];
        }
        paramsWithoutDefaultValues[queryParamKey].push(val);
      }
    });
  });
  return paramsWithoutDefaultValues;
};

export const getAllowedValuesForLimitedParams = (queryParams: Record<string, string[]>) => {
  const result: Record<string, string[]> = {};
  Object.keys(queryParams).forEach(param => {
    const values = queryParams[param];
    if (limitedCanonicalParams[param]) {
      result[param] = values.filter(value => limitedCanonicalParams[param].indexOf(value) > -1);
    } else {
      result[param] = values;
    }
  });
  return result;
};

export const getPermittedParamsLowercase = (queryParams: Record<string, string[]>) => {
  const filteredParams: Record<string, string[]> = {};

  orderedCanonicalParams.forEach(([possibleInputKeyVariants, canonicalKey]) => {
    possibleInputKeyVariants.forEach(variant => {
      if (queryParams[variant] !== undefined) {
        filteredParams[canonicalKey] = queryParams[variant];
      }
    });
  });

  return filteredParams;
};

export const deleteUnnecessaryCategoryParam = (path: string, params: Record<string, string[]>) => {
  const endingSlashRegex = /\/$/;
  const pathWithoutEndingSlash = path.replace(endingSlashRegex, '');
  const lastPathFragment = pathWithoutEndingSlash.substring(pathWithoutEndingSlash.lastIndexOf('/') + 1);

  keys.categories.forEach(categoryKey => {
    const categoryValues = params[categoryKey];

    if (categoryValues) {
      const newCategoryValues: string[] = [];

      categoryValues.forEach(categoryValue => {
        if (categoryValue !== lastPathFragment) {
          newCategoryValues.push(categoryValue);
        }
      });

      // eslint-disable-next-line no-param-reassign
      params[categoryKey] = newCategoryValues;
    }
  });

  return params;
};

export const roundSkipParam = (params: Record<string, string[]>) => {
  keys.skip.forEach(key => {
    if (params[key]) {
      const newValues: string[] = [];
      params[key].forEach(skipVal => {
        newValues.push(roundDownToMultipleOf60(skipVal));
      });
      // eslint-disable-next-line no-param-reassign
      params[key] = newValues;
    }
  });

  return params;
};

export const getOrderedParametersString = (queryParams: Record<string, string[]>): string => {
  const params: string[] = [];

  orderedCanonicalParams.forEach(([possibleInputKeyVariants, canonicalKey]) => {
    possibleInputKeyVariants.forEach(variant => {
      if (queryParams[variant] && queryParams[variant].length > 0) {
        queryParams[variant].forEach(param => {
          if (param?.length > 0) {
            params.push(`${canonicalKey}=${param}`);
          }
        });
      }
    });
  });

  return params.length === 0 ? '' : `?${params.join('&')}`;
};

const setLowerCaseKeys = (query: Record<string, string | string[] | boolean[]>) => {
  const queryWithLowerCaseKeys: Record<string, string[]> = {};
  Object.entries(query).forEach(([key, v]) => {
    const values: string[] = [];
    if (Array.isArray(v)) {
      v.forEach(x => {
        const xStr = x.toString();
        if (xStr.length > 0) {
          values.push(xStr);
        }
      });
    } else {
      const vStr = v.toString();
      if (vStr.length > 0) {
        values.push(vStr);
      }
    }
    if (values.length > 0) {
      queryWithLowerCaseKeys[key.toLowerCase()] = values;
    }
  });
  return queryWithLowerCaseKeys;
};

export const getCanonicalParams = (params: Record<string, string | string[] | boolean[]>, path: string): string => {
  const queryWithLowerCaseKeys = setLowerCaseKeys(params);
  const queryWithoutDefaults = removeDefaultParams(queryWithLowerCaseKeys);
  const permittedLowercaseParams = getPermittedParamsLowercase(queryWithoutDefaults);
  const paramsWithLimitedValues = getAllowedValuesForLimitedParams(permittedLowercaseParams);

  const paramsWithoutUnnecessaryCategoryParam = deleteUnnecessaryCategoryParam(path, paramsWithLimitedValues);
  const finalParams = roundSkipParam(paramsWithoutUnnecessaryCategoryParam);
  return getOrderedParametersString(finalParams);
};

export const getCanonicalUrl = (
  hostname: string,
  path: string,
  query: Record<string, string | string[] | boolean[]>,
): CanonicalUrl => {
  if (!hostname || !path || !query) {
    return null;
  }
  const paramsString = getCanonicalParams(query, path);
  const canonicalPath = path.replace(/\/$/, '');
  const hostAndPathStringWithoutSlash = `https://${hostname}${canonicalPath}`;

  return {
    fullUrl: encodeURI(`${hostAndPathStringWithoutSlash.toLowerCase()}/${paramsString}`),
    relativeUrl: encodeURI(`${canonicalPath.toLowerCase()}/${paramsString}`),
  };
};
